import { SearchOutlined } from '@ant-design/icons'
import { Empty, Tag } from 'antd'
import { ColumnsType, ColumnType } from 'antd/es/table'
import { EXPLORER_TABLE_ROW_BACKGROUND } from 'features/drilldown'
import {
  useFilters,
  useFilterToIds,
  useFocusItems,
} from 'features/drilldown/data/DrilldownFilterContext'
import { useVersionAwareDrilldownPanelData } from 'features/drilldown/data/DrilldownPanelDataContext'
import {
  useFieldKeys,
  useFilteredEntryQuery,
} from 'features/drilldown/rolodex/dataHelpers/drilldownDataHelpers'
import { GenericPanelData } from 'features/drilldown/types'
import {
  EnrichedOntologyFieldDefinition,
  OntologyRolodexEntryType,
  useOntology,
} from 'features/dynamicOntology'
import { usePaginatedQuery } from 'features/fde/helpers/usePaginatedQuery'
import uniq from 'lodash/uniq'
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import { GeckoEntry, RolodexEntryType } from 'types'

import { ActiveFilterSection } from './ActiveFilterSection'
import {
  DefaultRender,
  extractFieldsFromTemplate,
} from './columnRenders/TableDefaultColumnRender'
import {
  DrilldownTableExtraColumn,
  useDrilldownTableConfig,
} from './config/DrilldownTableConfigContext'
import { DrilldownTableColumnDef } from './config/DrilldownTableSchema'
import { ExplorerTableExportButton } from './export/ExplorerTableExportButton'
import { mergeAdditionalRolodexFilters } from './mergeAdditionalRolodexFilters'
import {
  PaginatedRolodexTable,
  PaginatedRolodexTableProps,
  PaginatedRolodexTableRecordType,
} from './PaginatedRolodexTable'
import {
  createTableFilters,
  ID_AND_NAME_FILTER_KEYS,
  ID_AND_NAME_SORT_KEYS,
} from './SharedTableComponents'

type DrilldownTablePanelSharedFields = {
  columnFilterValues: Readonly<Record<string, string>>
  propagateColumnFilters: boolean
  selectedRowKeys: ReadonlyArray<string>
  groupByType: OntologyRolodexEntryType
  columns: ReadonlyArray<DrilldownTableColumnDef>
  showFocusColumn: boolean
  enabledExtraColumnIds: ReadonlyArray<string>
}

export type DrilldownTablePanelData = GenericPanelData &
  DrilldownTablePanelSharedFields

const DRILLDOWN_TABLE_SCHEMA_VERSION = 5
// We omit any shared fields that should have initial values defined in the tables themselves
export const DEFAULT_TABLE_PANEL_SHARED_FIELD_DATA: Omit<
  DrilldownTablePanelData,
  'selectedColumns' | 'title'
> = {
  columnFilterValues: {},
  propagateColumnFilters: false,
  selectedRowKeys: [],
  groupByType: 'organization',
  columns: [],
  showFocusColumn: false,
  enabledExtraColumnIds: [],
  panelDataSchemaVersion: DRILLDOWN_TABLE_SCHEMA_VERSION,
  isPinned: true,
}

export const LINK_TEMPLATE_ASSET = '/assets/{site.id}/{unit.id}'
export const LINK_TEMPLATE_COMPONENT = '/details/{component.id}'

const TablePanelContainer = styled.div`
  padding: 0 1rem;
  background-color: ${EXPLORER_TABLE_ROW_BACKGROUND};
`

const EmptyStateContainer = styled.div`
  padding: 2rem 1rem;
`

const EXPORT_PLAN_PAGINATION = { pageSize: 10, currentPage: 1 }

function useLinkedTypes(
  groupByType: OntologyRolodexEntryType,
  columnDefs: ReadonlyArray<DrilldownTableColumnDef>,
  extraTypes?: ReadonlyArray<OntologyRolodexEntryType>
): {
  linkedTypes: ReadonlyArray<OntologyRolodexEntryType>
  childLinks: ReadonlyArray<OntologyRolodexEntryType>
} {
  const { fields, isDescendant } = useOntology()
  return useMemo(() => {
    const allTypes = uniq([
      ...columnDefs
        .flatMap(({ field, linkTemplate }) => {
          const linkTemplateFields = linkTemplate
            ? extractFieldsFromTemplate(linkTemplate)
            : []
          return [field, ...linkTemplateFields]
        })
        .map((field) => fields[field])
        .filter(
          (item): item is NonNullable<typeof item> =>
            item !== null && item !== undefined
        )
        .map((fd) => fd.entryType),
      ...(extraTypes ?? []),
    ])
    const linkedTypes = allTypes
      .filter((t) => isDescendant(groupByType, t))
      .filter((t) => t !== groupByType)
    const childLinks = allTypes
      .filter((t) => !linkedTypes.includes(t))
      .filter((t) => t !== groupByType)
    return { linkedTypes, childLinks }
  }, [columnDefs, extraTypes, fields, groupByType, isDescendant])
}

function createExtraColumn(
  col: DrilldownTableExtraColumn
): ColumnType<PaginatedRolodexTableRecordType> {
  return {
    title: col.name,
    key: col.id,
    width: col.width,
    render: (_, rec) => col.render({ rec }) as React.ReactNode,
  }
}

/**
 * This is an adapter layer between the paginated rolodex table and the individual table panels for things like validation, shared transformations, etc.
 *
 */
export const DrilldownTable: FC = () => {
  const {
    columnFilterValues: columnFilterValuesRec,
    propagateColumnFilters,
    selectedRowKeys,
    showFocusColumn,
    groupByType,
    setSelectedRowKeys,
    setPropagateColumnFilters,
    setColumnFilterValues,
    additionalRolodexFilters,
    paginatedTableOptions,
    columnDefs,
    extraColumns: extraColumnDefs,
    enabledExtraColumnIds,
    initialSorts,
  } = useDrilldownTableConfig()

  const { panelData } =
    useVersionAwareDrilldownPanelData<DrilldownTablePanelData>()

  const [selectedId, setSelectedId] = useState<string | undefined>(undefined)

  const focusSelectColumn = useMemo<
    ColumnType<{ id: string; entries: ReadonlyArray<GeckoEntry> }>
  >(
    () => ({
      render: (_, row) => (
        <Tag.CheckableTag
          checked={selectedId === row.id}
          onChange={(checked) => {
            const id = row.id
            setSelectedId(checked ? id : undefined)
          }}>
          <SearchOutlined />
        </Tag.CheckableTag>
      ),
      width: '80px',
    }),
    [selectedId]
  )

  const [, setFocusItems] = useFocusItems()
  useEffect(() => {
    if (paginatedTableOptions?.expandRecords) {
      return
    }
    setFocusItems(
      selectedId ? new Map().set(groupByType, [selectedId]) : undefined
    )
  }, [
    paginatedTableOptions?.expandRecords,
    selectedId,
    groupByType,
    setFocusItems,
  ])

  const selectedRowKeysAsSet = useMemo(
    () => new Set(selectedRowKeys),
    [selectedRowKeys]
  )
  useFilterToIds(
    paginatedTableOptions?.expandRecords || selectedRowKeysAsSet.size === 0
      ? null
      : selectedRowKeysAsSet,
    groupByType
  )

  const dataColumns = useMemo<
    ColumnsType<{ id: string; entries: ReadonlyArray<GeckoEntry> }>
  >(
    () =>
      columnDefs.map((cd) => {
        return {
          title: cd.title,
          key: cd.field,
          render: ({ entries }) => (
            <DefaultRender
              entries={entries}
              groupByType={groupByType}
              columnDef={cd}
            />
          ),
        }
      }),
    [columnDefs, groupByType]
  )
  const columns = useMemo(
    () => [...(showFocusColumn ? [focusSelectColumn] : []), ...dataColumns],
    [dataColumns, focusSelectColumn, showFocusColumn]
  )
  const { fields, isDescendant } = useOntology()
  const groupByAndAboveColumnFields = useMemo(
    () =>
      columnDefs
        .map(({ field }) => field)
        .filter(
          (field) =>
            fields[field] && isDescendant(groupByType, fields[field].entryType)
        ),
    [columnDefs, fields, groupByType, isDescendant]
  )
  const sortKeys = useFieldKeys(groupByAndAboveColumnFields)
  const canFilterOnField = useCallback(
    (fieldDef: EnrichedOntologyFieldDefinition) => fieldDef.type === 'text',
    []
  )
  const filterKeys = useFieldKeys(groupByAndAboveColumnFields, canFilterOnField)

  const columnFilterValues = useMemo<ReadonlyMap<string, string>>(
    () => new Map(Object.entries(columnFilterValuesRec ?? {})),
    [columnFilterValuesRec]
  )

  const columnFilterKeyToLabel = useMemo<ReadonlyMap<string, ReactNode>>(() => {
    return new Map(
      Object.keys(filterKeys ?? ID_AND_NAME_FILTER_KEYS ?? {}).map((key) => {
        const colTitle = columns?.find((c) => c.key === key)?.title
        const isValidTitle =
          colTitle &&
          typeof colTitle !== 'function' &&
          typeof colTitle !== 'boolean' &&
          typeof colTitle !== 'number'
        return [key, isValidTitle ? colTitle : <>{key}</>]
      })
    )
  }, [columns, filterKeys])
  const rolodexColumnFilterValues = useMemo(
    () => createTableFilters(columnFilterValues, filterKeys, groupByType),
    [filterKeys, groupByType, columnFilterValues]
  )
  useFilters(propagateColumnFilters ? rolodexColumnFilterValues : undefined)
  useEffect(() => {
    if (!columnFilterValues.size) {
      setPropagateColumnFilters?.(false)
    }
  }, [setPropagateColumnFilters, columnFilterValues])

  const { query, isLoading } = useFilteredEntryQuery(groupByType)

  // Single source of truth for "this panel applied additional scoping":
  // both the additional filters and the entry types they target ride
  // together inside this query. Any consumer that uses it gets matching
  // `filters` and `links` for free.
  const mergedRolodexQuery = useMemo(
    () => mergeAdditionalRolodexFilters(query, additionalRolodexFilters),
    [additionalRolodexFilters, query]
  )

  const extraColumnTypes = useMemo<ReadonlyArray<OntologyRolodexEntryType>>(
    () =>
      uniq([
        ...enabledExtraColumnIds.flatMap(
          (id) =>
            extraColumnDefs.find((def) => def.id === id)?.linked_types ?? []
        ),
      ]),
    [enabledExtraColumnIds, extraColumnDefs]
  )
  const extraColumns = useMemo(
    () =>
      extraColumnDefs
        .filter((def) => enabledExtraColumnIds.includes(def.id))
        .map((def) => createExtraColumn(def)),
    [enabledExtraColumnIds, extraColumnDefs]
  )
  const enabledExtraColumnDefs = useMemo(
    () =>
      extraColumnDefs.filter((def) => enabledExtraColumnIds.includes(def.id)),
    [enabledExtraColumnIds, extraColumnDefs]
  )
  const extraColumnSortKeys = useMemo<PaginatedRolodexTableProps['sortKeys']>(
    () =>
      Object.fromEntries(
        extraColumnDefs
          .filter((def) => enabledExtraColumnIds.includes(def.id))
          .flatMap((def) => (def.sortKey ? [[def.id, def.sortKey]] : []))
      ),
    [enabledExtraColumnIds, extraColumnDefs]
  )
  const tableSortKeys = useMemo(
    () => ({ ...(sortKeys ?? ID_AND_NAME_SORT_KEYS), ...extraColumnSortKeys }),
    [extraColumnSortKeys, sortKeys]
  )
  const { linkedTypes, childLinks } = useLinkedTypes(
    groupByType,
    columnDefs,
    extraColumnTypes
  )
  const initialPagination = useMemo(
    () =>
      paginatedTableOptions?.defaultPageSize
        ? {
            pageSize: paginatedTableOptions.defaultPageSize,
          }
        : undefined,
    [paginatedTableOptions?.defaultPageSize]
  )

  // Export query planning is separate from the table's paginated entry
  // loading. We base it on `mergedRolodexQuery` so panel-injected filters
  // (`additionalRolodexFilters`) scope the export the same way they scope
  // the on-screen table; the additional links those filters need are
  // already baked in by `mergeAdditionalRolodexFilters`. Column-derived
  // links are layered in here for the export's own histogram. The export
  // still intentionally ignores the table's column-search filters and sort
  // state so it always reflects the underlying drilldown selection.
  const exportRolodexQuery = useMemo(
    () => ({
      ...mergedRolodexQuery,
      links: uniq([
        ...(mergedRolodexQuery?.links ?? []),
        ...linkedTypes.filter((t) => t !== mergedRolodexQuery?.type),
      ]) as RolodexEntryType[],
    }),
    [linkedTypes, mergedRolodexQuery]
  )
  const {
    isLoading: planLoading,
    totalEntries,
    truncated,
    orderedGroupIds,
    groupIdToBaseId,
    links: resolvedLinks,
  } = usePaginatedQuery(exportRolodexQuery, EXPORT_PLAN_PAGINATION, groupByType)
  const exportButton = useMemo(
    () => (
      <ExplorerTableExportButton
        groupByType={groupByType}
        links={resolvedLinks}
        childLinks={childLinks}
        orderedGroupIds={orderedGroupIds}
        groupIdToBaseId={groupIdToBaseId}
        expandRecords={paginatedTableOptions?.expandRecords}
        truncated={truncated}
        totalEntries={totalEntries}
        isPlanLoading={planLoading || isLoading}
        columnDefs={columnDefs}
        enabledExtraColumns={enabledExtraColumnDefs}
        panelTitle={panelData.title}
      />
    ),
    [
      childLinks,
      columnDefs,
      enabledExtraColumnDefs,
      groupByType,
      groupIdToBaseId,
      paginatedTableOptions?.expandRecords,
      isLoading,
      orderedGroupIds,
      panelData.title,
      planLoading,
      resolvedLinks,
      totalEntries,
      truncated,
    ]
  )

  const hasNoConfiguredColumns =
    columnDefs.length === 0 && extraColumns.length === 0

  return (
    <TablePanelContainer>
      {hasNoConfiguredColumns ? (
        <EmptyStateContainer>
          <Empty description='Add columns from the panel configuration to start' />
        </EmptyStateContainer>
      ) : (
        <PaginatedRolodexTable
          groupByType={groupByType}
          columns={columns}
          sortKeys={tableSortKeys}
          filterKeys={filterKeys}
          columnFilterValues={columnFilterValues}
          setColumnFilterValues={setColumnFilterValues}
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={
            paginatedTableOptions?.expandRecords
              ? undefined
              : setSelectedRowKeys
          }
          rolodexQuery={mergedRolodexQuery}
          linkedTypes={linkedTypes}
          childLinks={childLinks}
          loading={isLoading}
          extraColumns={extraColumns}
          initialSortingOptions={initialSorts}
          expandRecords={paginatedTableOptions?.expandRecords}
          tableRowKey={paginatedTableOptions?.tableRowKey}
          pagination={initialPagination}
          tableFooterComponent={exportButton}
        />
      )}
      <ActiveFilterSection
        columnFilterValues={columnFilterValues}
        columnFilterKeyToLabel={columnFilterKeyToLabel}
        propagateColumnFilters={propagateColumnFilters}
        setPropagateColumnFilters={setPropagateColumnFilters}
        rowFilterIds={selectedRowKeys}
      />
    </TablePanelContainer>
  )
}
